/**
 * Load / dual-write fax questionnaire (PG + Firestore).
 *
 * Reads: PG `submission.questionnaire_details`. Firestore
 * `questionnaire/fax_questionnaire` only when PG cannot be queried (no org).
 * Writes: full-replace both stores (matches prepare semantics; fax submit
 * still reads Firestore until backend is PG-driven).
 *
 * @module procedureQuestionaire/utils/faxQuestionnairePersistence
 */
import { FirestoreService } from "../../../api/firebase/firestoreService";
import { SingleOrderDetailsGenericUpdateResponse } from "../../../api/postgreSQL/postCalls/singleOrderDetailsGenericUpdateForPostgres";
import { updateQuestionTreeWithAnswers } from "./updateQuestionTreeWithAnswers";

/** Result shape returned by `useUpdateOrderDetails` / `useAbortableFetch`. */
type UpdateFetchResult = {
  success: boolean;
  data: SingleOrderDetailsGenericUpdateResponse | null;
  error: unknown;
};

/**
 * Returns true when `value` looks like a real questionnaire doc (has a tree).
 *
 * @param value - Raw `questionnaire_details` from PG or a Firestore doc
 * @returns Whether the value is usable as fax questionnaire state
 */
export const hasQuestionnaireTree = (
  value: unknown,
): value is Record<string, unknown> => {
  if (!value || typeof value !== "object") {
    return false;
  }
  const tree = (value as Record<string, unknown>).question_tree;
  return Array.isArray(tree);
};

/**
 * Resolves the fax questionnaire from already-loaded PG `questionnaire_details`.
 *
 * When `orgId` is present, PG was queried: an empty tree means prepare has not
 * written (or a form re-select cleared PG). Do not fall back to Firestore —
 * that can apply last time's completed tree, freeze the poller, and hide the
 * new prepare. Firestore is only read when PG cannot be queried (no org).
 *
 * @param orderId - Medical PA order id
 * @param pgDetails - `submission.questionnaire_details` already loaded (may be empty)
 * @param orgId - Tenant org id; when present, skip Firestore on a PG miss
 * @returns The questionnaire doc, or null when PG has no tree (or Firestore
 *   has none, if PG was skipped)
 */
export const loadFaxQuestionnaireDocument = async (
  orderId: string,
  pgDetails: unknown,
  orgId?: string | null,
): Promise<Record<string, unknown> | null> => {
  if (hasQuestionnaireTree(pgDetails)) {
    return pgDetails;
  }

  if (orgId) {
    return null;
  }

  const firestoreDoc = await FirestoreService.getDocument<
    Record<string, unknown>
  >(`/medical_pa_orders/${orderId}/questionnaire/fax_questionnaire`);
  if (hasQuestionnaireTree(firestoreDoc)) {
    return firestoreDoc;
  }

  return null;
};

/**
 * Merges UI answers into the questionnaire and dual-writes Firestore + PG.
 *
 * Firestore is required so `/fax/submit` (still Firestore-backed) sees reviewer
 * edits. Postgres keeps `submission.questionnaire_details` in sync for the
 * PG migration path.
 *
 * @param params - Order context, answers, and the PG update runner
 * @returns The full questionnaire payload that was persisted
 * @throws {Error} When org_id is missing, write fails, or no submission row updated
 */
export const persistFaxQuestionnaire = async (params: {
  orderId: string;
  orgId: string | null | undefined;
  updatedData: Record<string, unknown>;
  currentAnswers: { id: string; answer: any }[];
  updatedBy: string;
  updateSubmissionQuestionnaire: (payload: {
    questionnaire_details: Record<string, unknown>;
  }) => Promise<UpdateFetchResult>;
}): Promise<Record<string, unknown>> => {
  const {
    orderId,
    orgId,
    updatedData,
    currentAnswers,
    updatedBy,
    updateSubmissionQuestionnaire,
  } = params;

  const updateData: Record<string, unknown> = {
    ...updatedData,
    question_tree: updateQuestionTreeWithAnswers(
      (updatedData.question_tree as any[]) || [],
      currentAnswers,
    ),
    updated_at: new Date().toISOString(),
    updated_by: updatedBy,
  };

  await FirestoreService.updateDocument(
    `medical_pa_orders/${orderId}/questionnaire`,
    "fax_questionnaire",
    updateData,
  );

  if (!orgId) {
    throw new Error(
      "org_id is required to persist questionnaire_details to Postgres",
    );
  }

  const pgResult = await updateSubmissionQuestionnaire({
    questionnaire_details: updateData,
  });

  if (!pgResult.success || pgResult.error) {
    throw (
      pgResult.error ??
      new Error("Failed to persist questionnaire_details to Postgres")
    );
  }
  if (!pgResult.data?.success || (pgResult.data.updated_rows ?? 0) < 1) {
    throw new Error(
      "No submission row updated for questionnaire_details; dump latest_form_data first",
    );
  }

  return updateData;
};
